import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProcessFacade } from './process.facade';
import { MainFacade } from '../../main.facade';
import { Dropdown } from 'primeng/dropdown';
import { ProcessType } from '../../models/request';
import { ProcessService, ToolAvailabilityStatus } from '../../../services/process.service';
import {
  getProcessDropdownGroups,
  filterByActiveTools,
  IProcessGroup,
  IProcessType,
} from '../../models/jit-tool-registry.helpers';

@Component({
  selector: 'process',
  templateUrl: './process.component.html',
  styleUrls: ['./process.component.scss'],
})
export class ProcessComponent implements OnInit, OnDestroy {
  public ProcessType = ProcessType;
  private readonly destroy$ = new Subject<void>();

  public activeToolNames: string[] = [];

  /** Null until the server has first answered. */
  public availability: ToolAvailabilityStatus | null = null;

  readonly NO_QUEUE_MESSAGE = 'Analysis queue is not available, please contact your support representative.';
  readonly NO_IO_MESSAGE = 'No file service (IO) running on the server — please contact your support representative.';

  readonly TOOL_GROUPS: IProcessGroup[] = getProcessDropdownGroups();

  private get ALL_TOOLS(): IProcessType[] {
    return this.TOOL_GROUPS.flatMap((g) => g.items);
  }

  public selectedType: any = null;

  disableProcess = true;

  @ViewChild(Dropdown) processTypeDropdown!: Dropdown;

  constructor(
    public mainFacade: MainFacade,
    public processFacade: ProcessFacade,
    public processService: ProcessService,
  ) {}

  ngOnInit(): void {
    const type: ProcessType | null = this.processFacade.getSelectedType();

    // No fallback to all tools: an empty list means none can run (for example, the IO worker
    // is down, so nothing could download or upload a file).
    this.processService
      .getToolAvailability$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ status, tools }) => {
        this.availability = status;
        this.activeToolNames = tools;
      });

    this.selectedType = this.ALL_TOOLS.find((element) => element.type === type);
    this.processFacade.trackLoadedFiles(this.mainFacade);
    this.mainFacade
      .canProcessSelection$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((canProcess) => {
        this.disableProcess = !canProcess;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getAvailableGroups(): IProcessGroup[] {
    return filterByActiveTools(this.TOOL_GROUPS, this.activeToolNames);
  }

  onProcessSelect(event: any) {
    this.selectedType = event.value;
    this.processFacade.setSelectedType(event.value.type);
  }

  getType(selectedType: any): ProcessType | null {
    if (selectedType) {
      return selectedType.type;
    }
    return null;
  }
}
